import Analytics, { AnalyticsInstance } from "analytics";
import React, { createContext, useContext, useEffect, useState } from "react";
// @ts-expect-error No types available for this package
import googleAnalytics from "@analytics/google-analytics";

interface MetaPixelConfig {
  pixelId: string;
  options?: {
    autoConfig?: boolean;
    debug?: boolean;
  };
}

// Custom Meta Pixel plugin for DavidWells analytics
function metaPixelPlugin({ pixelId, options }: MetaPixelConfig) {
  return {
    name: "meta-pixel",
    config: {
      options,
      pixelId,
    },
    initialize: ({ config }: { config: MetaPixelConfig }) => {
      const { pixelId, options } = config;
      if (!pixelId) return undefined;

      // Check if fbq already exists
      if ((window as any).fbq) {
        (window as any).fbq("init", pixelId);
        (window as any).fbq("track", "PageView");
        return;
      }

      // Create fbq stub function to queue events until script loads
      const fbq: any = (...args: any[]) => {
        if (fbq.callMethod) {
          fbq.callMethod(...args);
        } else {
          fbq.queue.push(args);
        }
      };

      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];

      (window as any).fbq = fbq;
      (window as any)._fbq = fbq;

      // Create and load the script
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";

      // Insert the script
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }

      // Initialize pixel (events will be queued until script loads)
      (window as any).fbq("init", pixelId);
      if (options?.autoConfig) {
        (window as any).fbq("set", "autoConfig", true, pixelId);
      }
      (window as any).fbq("track", "PageView");
    },

    page: () => {
      if (typeof (window as any).fbq !== "undefined") {
        (window as any).fbq("track", "PageView");
      }
    },

    track: ({ payload }: { payload: any }) => {
      const fbq = (window as any).fbq;
      if (typeof fbq === "undefined") return;

      const { event, properties } = payload;

      // Map GA4 e-commerce events to Meta Pixel events
      const metaEventMap: Record<string, string | null> = {
        view_item_list: "ViewContent",
        view_item: "ViewContent",
        select_item: "ViewContent",
        add_to_cart: "AddToCart",
        remove_from_cart: null,
        view_cart: "ViewContent",
        begin_checkout: "InitiateCheckout",
        add_shipping_info: "AddPaymentInfo",
        add_payment_info: "AddPaymentInfo",
        purchase: "Purchase",
        search: "Search",
      };

      const metaEvent = metaEventMap[event];
      if (metaEvent === null) return;

      // Transform properties to Meta format
      const metaProperties: Record<string, any> = {};

      if (properties?.items && Array.isArray(properties.items)) {
        metaProperties.content_ids = properties.items.map(
          (item: any) => item.item_id
        );
        metaProperties.content_type = "product";
        metaProperties.contents = properties.items.map((item: any) => ({
          id: item.item_id,
          quantity: item.quantity || 1,
        }));
      }

      if (properties?.value !== undefined) {
        metaProperties.value = properties.value;
      }

      if (properties?.currency) {
        metaProperties.currency = properties.currency;
      }

      if (properties?.search_term) {
        metaProperties.search_string = properties.search_term;
      }

      if (properties?.transaction_id) {
        metaProperties.order_id = properties.transaction_id;
      }

      const finalEvent = metaEvent || event;
      fbq("track", finalEvent, metaProperties);
    },

    identify: () => {},

    loaded: () => typeof (window as any).fbq !== "undefined",
  };
}

// TikTok Pixel plugin for DavidWells analytics
interface TikTokPixelConfig {
  pixelId: string;
  options?: Record<string, any>;
}

function tiktokPixelPlugin({ pixelId, options }: TikTokPixelConfig) {
  return {
    name: "tiktok-pixel",
    config: {
      options,
      pixelId,
    },
    initialize: ({ config }: { config: TikTokPixelConfig }) => {
      const { pixelId, options } = config;
      if (!pixelId) return undefined;

      // Load TikTok Pixel script and initialize
      (function (w: any, d: Document, t: string) {
        w.TiktokAnalyticsObject = t;
        const ttq = (w[t] = w[t] || []);
        ttq.methods = [
          "page",
          "track",
          "identify",
          "instances",
          "debug",
          "on",
          "off",
          "once",
          "ready",
          "alias",
          "group",
          "enableCookie",
          "disableCookie",
        ];
        ttq.setAndDefer = function (tObj: any, e: string) {
          tObj[e] = (...args: any[]) => {
            tObj.push([e, ...args]);
          };
        };
        for (let i = 0; i < ttq.methods.length; i++) {
          ttq.setAndDefer(ttq, ttq.methods[i]);
        }
        ttq.instance = function (tId: string) {
          const e = ttq._i[tId] || [];
          for (let n = 0; n < ttq.methods.length; n++) {
            ttq.setAndDefer(e, ttq.methods[n]);
          }
          return e;
        };
        ttq.load = function (e: string, n?: Record<string, any>) {
          const i = "https://analytics.tiktok.com/i18n/pixel/events.js";
          ttq._i = ttq._i || {};
          ttq._i[e] = [];
          ttq._i[e]._u = i;
          ttq._t = ttq._t || {};
          ttq._t[e] = +new Date();
          ttq._o = ttq._o || {};
          ttq._o[e] = n || {};
          const o = d.createElement("script");
          o.type = "text/javascript";
          (o as any).async = true;
          (o as any).src = i;
          const a = d.getElementsByTagName("script")[0];
          a.parentNode?.insertBefore(o, a);
        };
      })(window as any, document, "ttq");

      (window as any).ttq.load(pixelId, options || {});
      (window as any).ttq.page();
    },

    page: () => {
      if (typeof (window as any).ttq !== "undefined") {
        (window as any).ttq.page();
      }
    },

    track: ({ payload }: { payload: any }) => {
      const ttq = (window as any).ttq;
      if (typeof ttq === "undefined") return;

      const { event, properties } = payload;

      // Map GA4 e-commerce events to TikTok Pixel events
      const tiktokEventMap: Record<string, string | null> = {
        view_item_list: "ViewContent",
        view_item: "ViewContent",
        select_item: "ViewContent",
        add_to_cart: "AddToCart",
        remove_from_cart: null,
        view_cart: "ViewContent",
        begin_checkout: "InitiateCheckout",
        add_shipping_info: "AddPaymentInfo",
        add_payment_info: "AddPaymentInfo",
        purchase: "CompletePayment",
        search: "Search",
        // Legacy event mappings
        "Product Viewed": "ViewContent",
        "Product Added": "AddToCart",
        "Checkout Started": "InitiateCheckout",
        "Order Completed": "CompletePayment",
      };

      const tiktokEvent = tiktokEventMap[event];
      if (tiktokEvent === null) return;

      // Transform properties to TikTok format
      const tiktokProperties: Record<string, any> = {};

      if (properties?.items && Array.isArray(properties.items)) {
        tiktokProperties.contents = properties.items.map((item: any) => ({
          content_id: item.item_id,
          content_name: item.item_name,
          content_category: item.item_category,
          quantity: item.quantity || 1,
          price: item.price,
        }));
        tiktokProperties.content_type = "product";
      }

      if (properties?.value !== undefined) {
        tiktokProperties.value = properties.value;
      }

      if (properties?.currency) {
        tiktokProperties.currency = properties.currency;
      }

      if (properties?.search_term) {
        tiktokProperties.query = properties.search_term;
      }

      const finalEvent = tiktokEvent || event;
      ttq.track(finalEvent, tiktokProperties);
    },

    identify: ({ payload }: { payload: any }) => {
      if (typeof (window as any).ttq !== "undefined") {
        const { userId, traits } = payload || {};
        if ((window as any).ttq.identify) {
          (window as any).ttq.identify({ userId, ...traits });
        }
      }
    },

    loaded: () => typeof (window as any).ttq !== "undefined",
  };
}

interface AnalyticsContextType {
  analytics: AnalyticsInstance | null;
  isInitialized: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  analytics: null,
  isInitialized: false,
});

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsInstance | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Read analytics IDs from DOM (injected by Cloudflare Worker)
    const getAnalyticsConfig = () => {
      // Try to get from meta tags first
      const gaId = document
        .querySelector('meta[name="google-analytics-id"]')
        ?.getAttribute("content");
      const fbPixelId = document
        .querySelector('meta[name="meta-pixel-id"]')
        ?.getAttribute("content");
      const ttPixelId = document
        .querySelector('meta[name="tiktok-pixel-id"]')
        ?.getAttribute("content");

      return {
        googleAnalyticsId: gaId,
        metaPixelId: fbPixelId,
        tiktokPixelId: ttPixelId,
      };
    };

    const initializeAnalytics = () => {
      const { googleAnalyticsId, metaPixelId, tiktokPixelId } = getAnalyticsConfig();

      const plugins: any[] = [];

      // Add Google Analytics plugin if ID is available
      if (googleAnalyticsId) {
        plugins.push(
          googleAnalytics({
            measurementIds: [googleAnalyticsId],
          })
        );
      }

      // Add Meta Pixel plugin if ID is available
      if (metaPixelId) {
        plugins.push(
          metaPixelPlugin({
            pixelId: metaPixelId,
            options: {
              autoConfig: true,
            },
          })
        );
      }

      // Add TikTok Pixel plugin if ID is available
      if (tiktokPixelId) {
        plugins.push(
          tiktokPixelPlugin({
            pixelId: tiktokPixelId,
            options: {},
          })
        );
      }

      // Only initialize if we have at least one plugin
      if (plugins.length > 0) {
        const analyticsInstance = Analytics({
          app: "vendor-app",
          plugins,
        });

        setAnalytics(analyticsInstance);
        setIsInitialized(true);

        console.log(
          "Analytics initialized with plugins:",
          plugins.map((p) => p.name || "meta-pixel")
        );
      } else {
        console.warn(
          "No analytics IDs found in DOM. Analytics not initialized."
        );
      }
    };

    // Initialize immediately if DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initializeAnalytics);
    } else {
      initializeAnalytics();
    }

    return () => {
      document.removeEventListener("DOMContentLoaded", initializeAnalytics);
    };
  }, []);

  return (
    <AnalyticsContext.Provider value={{ analytics, isInitialized }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
